import {ChangeDetectorRef, Component, ElementRef, HostListener, OnInit} from '@angular/core';
import {FormControl} from "@angular/forms";
import {BackendApiService} from "../../../services/backend-api.service";
import {environment} from "../../../../environments/environment";
import {getUserInitials} from "../../../utils/helpers";
import {debounceTime, distinctUntilChanged, EMPTY, switchMap} from "rxjs";

@Component({
    selector: 'app-navbar-search',
    templateUrl: './navbar-search.component.html',
    styleUrls: ['./navbar-search.component.scss']
})
export class NavbarSearchComponent implements OnInit {

    CDN_GLOBAL = environment.CDN_GLOBAL;
    getUserInitials = getUserInitials;

    searchControl = new FormControl();

    private _results?: SearchResult[];
    truncatedSet = new Set<number>();

    get results() { return this._results; }
    set results(val: SearchResult[] | undefined) {
        this._results = val;
        this.truncatedSet.clear();
        if (val?.length) {
            setTimeout(() => {
                this.elementRef.nativeElement.querySelectorAll('.result-name')
                    .forEach((el: HTMLElement, i: number) => {
                        if (el.scrollWidth > el.clientWidth) this.truncatedSet.add(i);
                    });
                this.cdr.detectChanges();
            });
        }
    }

    selectedIndex: number = -1;

    isLoading: boolean;
    error: any;

    contextMenuVisible = false;
    contextMenuX = 0;
    contextMenuY = 0;
    contextMenuItem: SearchResult | null = null;
    contextMenuActions = [{ label: 'Open in new tab', value: 'open' }];

    constructor(
        private backendApiService: BackendApiService,
        private elementRef: ElementRef,
        private cdr: ChangeDetectorRef,
    ) {
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const clickedInside = this.elementRef.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.results = undefined;
        }
    }

    @HostListener('document:visibilitychange')
    onVisibilityChange() {
        if (!document.hidden) {
            this.restoreResultsIfNeeded();
        }
    }

    @HostListener('window:pageshow', ['$event'])
    onPageShow(event: PageTransitionEvent) {
        if (event.persisted) {
            this.restoreResultsIfNeeded();
        }
    }

    private restoreResultsIfNeeded() {
        const input = this.elementRef.nativeElement.querySelector('input');
        const searchText = input?.value || this.searchControl.value;
        if (searchText?.trim().length > 1 && !this.results) {
            this.searchControl.setValue(searchText, { emitEvent: false });
            this.loadSearchResults(searchText);
        }
    }

    ngOnInit() {
        this.searchControl.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(searchText => {
                this.selectedIndex = -1;
                const trimmed = (searchText || '').trim();
                if (trimmed.length <= 1) {
                    this.results = undefined;
                    this.isLoading = false;
                    return EMPTY;
                }
                this.isLoading = true;
                return this.backendApiService.get('/search?text=' + encodeURIComponent(searchText));
            })
        ).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                this.error = null;
                this.results = response.results;
            },
            error: (e: any) => {
                this.isLoading = false;
                this.error = e;
                this.results = undefined;
            }
        });
    }

    onKeyDown(event: KeyboardEvent) {
        if (!this.results?.length) return;

        if (event.key === 'Escape') {
            this.results = undefined;
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.selectedIndex = (this.selectedIndex + 1) % this.results.length;
            this.scrollSelectedIntoView();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.selectedIndex = (this.selectedIndex - 1 + this.results.length) % this.results.length;
            this.scrollSelectedIntoView();
        } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
            event.preventDefault();
            window.location.href = this.getResultUrl(this.results[this.selectedIndex]);
        }
    }

    private scrollSelectedIntoView() {
        setTimeout(() => {
            const items = this.elementRef.nativeElement.querySelectorAll('.dropdown-item');
            const active = items[this.selectedIndex] as HTMLElement;
            active?.scrollIntoView({ block: 'nearest' });
        });
    }

    loadSearchResults(searchText: string) {
        if (!(searchText || '').trim().length) {
            return this.results = undefined;
        }

        this.isLoading = true;

        this.backendApiService
            .get('/search?text=' + encodeURIComponent(searchText))
            .subscribe({
                next: (response: any) => {
                    this.isLoading = false;
                    this.error = null;
                    this.results = response.results;
                },
                error: (e: any) => {
                    this.isLoading = false;
                    this.error = e;
                    this.results = undefined;
                },
            });
    }

    clearSearch() {
        this.searchControl.setValue('');
        this.results = undefined;
    }

    onRightClick(event: MouseEvent, result: SearchResult) {
        event.preventDefault();
        this.contextMenuVisible = true;
        this.contextMenuX = event.clientX;
        this.contextMenuY = event.clientY;
        this.contextMenuItem = result;
    }

    onContextMenuAction(action: { value: string; item: SearchResult }) {
        if (action.value === 'open') {
            window.open(this.getResultUrl(action.item), '_blank');
        }
        this.contextMenuVisible = false;
    }

    onContextMenuClose() {
        this.contextMenuVisible = false;
    }

    getImageInitials(result: SearchResult): string {
        const initialsTypes = ['User', 'Freelancer', 'Person', 'Brand', 'Partner', 'Aff System', 'Game', 'Game Provider'];
        return initialsTypes.includes(result.type) ? getUserInitials(result.name) : '';
    }

    getImagePurpose(type: string): string {
        const map: Record<string, string> = {
            'User': 'user', 'Freelancer': 'user', 'Person': 'user',
            'Website': 'website',
            'Brand': 'brand',
            'Partner': 'partner',
            'Account': 'account',
            'Campaign': 'campaign',
            'Aff System': 'aff-system',
            'Platform': 'platform',
            'GoLink': 'golink',
            'Game': 'game',
            'Game Provider': 'game-provider',
            'Lead': 'lead',
            'Initiative': 'initiative',
        };
        return map[type] || '';
    }

    getResultUrl(result: SearchResult): string {
        if (!result.path.startsWith('/cdb/')) {
            return `/new_cdb${result.path}`;
        }

        return result.path;
    }
}

interface SearchResult {
    type: string;
    name: string;
    image: string;
    path: string;
    active: number;
    subtitle: string | null;
}
